import { type CSSProperties, type ReactNode, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/utils/cn'

export interface TooltipProps {
  /** The tip's text. Nothing is shown — and nothing wraps differently — while it is empty. */
  readonly content: ReactNode
  readonly children: ReactNode
  /**
   * Opens only when this returns true for the anchor at hover time — how
   * `TruncatedText` shows its tip only while the line is actually cut off.
   */
  readonly shouldOpen?: (anchor: HTMLSpanElement) => boolean
  /** Layout of the anchor box; `inline-flex` keeps a wrapped button in place. */
  readonly className?: string
}

/** Gap between the anchor and the tip, and the minimum gap to the viewport edge. */
const OFFSET_PX = 8

/**
 * A dark tip above the wrapped element (below when there is no room above),
 * on hover and on keyboard focus; Escape or leaving closes it.
 *
 * **Works on disabled controls**, which is its main job: a browser fires no
 * hover events on a disabled button or input, so a `title` there never shows.
 * Disabled descendants are made pointer-transparent, so the hover reaches this
 * wrapper, and the wrapper itself becomes focusable while it carries a tip so
 * keyboard users can read why the control is off (`aria-describedby`).
 *
 * Portaled with fixed positioning: table cells and cards clip overflow.
 */
export function Tooltip({ content, children, shouldOpen, className }: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<{ style: CSSProperties; above: boolean }>()
  const anchorRef = useRef<HTMLSpanElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const tipId = useId()
  const hasContent =
    content !== null && content !== undefined && content !== '' && content !== false

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(undefined)
      return
    }
    const reposition = () => {
      const anchor = anchorRef.current?.getBoundingClientRect()
      const tip = tipRef.current
      if (!anchor || !tip) return
      const above = anchor.top - OFFSET_PX >= tip.offsetHeight
      const centered = anchor.left + anchor.width / 2 - tip.offsetWidth / 2
      const left = Math.min(
        Math.max(OFFSET_PX, centered),
        Math.max(OFFSET_PX, window.innerWidth - tip.offsetWidth - OFFSET_PX),
      )
      setPosition({
        above,
        style: above
          ? { bottom: window.innerHeight - anchor.top + OFFSET_PX, left }
          : { top: anchor.bottom + OFFSET_PX, left },
      })
    }
    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [isOpen])

  const open = () => {
    const anchor = anchorRef.current
    if (!hasContent || !anchor) return
    if (shouldOpen && !shouldOpen(anchor)) return
    setIsOpen(true)
  }
  const close = () => setIsOpen(false)

  return (
    <>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: hover / focus only reveal supplementary text */}
      <span
        ref={anchorRef}
        aria-describedby={isOpen ? tipId : undefined}
        // Focusable only while it explains something: a disabled control inside cannot take focus.
        tabIndex={hasContent && !shouldOpen ? 0 : undefined}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        onKeyDown={(event) => {
          if (event.key === 'Escape') close()
        }}
        className={cn(
          // A disabled descendant swallows hover; let it fall through to this wrapper.
          '[&_:disabled]:pointer-events-none',
          'rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
          className ?? 'inline-flex',
        )}
      >
        {children}
      </span>

      {isOpen &&
        createPortal(
          <div
            ref={tipRef}
            id={tipId}
            role="tooltip"
            style={position?.style}
            className={cn(
              'pointer-events-none fixed z-50 max-w-xs rounded-md bg-fg px-2.5 py-1.5 text-xs font-medium text-fg-inverted shadow-lg',
              // Invisible until measured — avoids a one-frame jump from (0, 0).
              position === undefined && 'invisible',
            )}
          >
            {content}
            <span
              aria-hidden
              className={cn(
                'absolute left-1/2 size-2 -translate-x-1/2 rotate-45 bg-fg',
                position?.above === false ? '-top-1' : '-bottom-1',
              )}
            />
          </div>,
          document.body,
        )}
    </>
  )
}
